import { useId, useMemo } from 'react'
import type { Pass } from '@lgc/database/types/domains/pass'
import type { PassPlanDetails } from '@lgc/database/types/domains/subscription'
import { Card } from '#primitives/data-display'
import { GradientOverlay } from '#primitives/data-display'
import {
  gradientVariants,
  type GradientVariant,
} from '#primitives/data-display'
import { PassOrnamentIcon } from '#icons'
import { cn } from '#utils'
import { formatSubscriptionPrice, ZERO_PRICE_LABEL, INSTANCE_ZERO_PRICE_LABEL } from './formatSubscriptionPrice'

type PassCardDisplay = Pick<PassPlanDetails, 'id' | 'name' | 'description' | 'color' | 'price'>

type PassCardProps<T extends PassCardDisplay> = {
  /** Accepts full Pass objects or the PassPlanDetails subset (e.g. from the active subscription API) */
  pass: T
  /**
   * What this member actually paid, when the card shows someone's own pass.
   * Both `Pass` and `PassPlanDetails` are *templates*, so `pass.price` is what
   * the pass normally costs — an admin may have comped or discounted it. Pass
   * `subscription.price` here for any "my plan"/active surface; omit it on
   * catalog surfaces, where the template price is the right answer.
   *
   * `'0'`/`0` renders the zero label (see `formatSubscriptionPrice`).
   * @unit dollars
   */
  price?: string | number | null
  /** Optional badge text displayed above the title (e.g. "Active") */
  badge?: string
  onClick?(pass: T): void
  className?: string
  /** Override the aria-label on the card's interactive overlay button. Defaults to "Edit {name}" */
  overlayLabel?: string
  /** Whether the overlay button opens a dialog. Set to false for non-dialog interactions (e.g. plan selection). Defaults to true */
  opensDialog?: boolean
  /** Controls the card's display context. 'my-plan' enables additional UI treatment for the member's active plan. Defaults to 'default' */
  variant?: 'default' | 'my-plan' | 'reporting'
  /** A currency-aware price supplied by a reporting response; preserves catalog defaults. */
  formattedPrice?: string
}

export const PassCard = <T extends PassCardDisplay = Pass | PassPlanDetails>({ pass, price, badge, onClick, className, overlayLabel, opensDialog = true, variant = 'default', formattedPrice }: PassCardProps<T>) => {
  const gradientVariantKey = pass.color?.toLowerCase() as GradientVariant
  const gradient = gradientVariants[gradientVariantKey] ?? gradientVariants.purple

  const priceText = useMemo(() => {
    // `??` not `||`: '0'/'0.00' are truthy strings but a comped pass must not
    // fall through to the template price. An explicit `price` marks a member's
    // own pass (instance) → render "$0.00"; a bare template (catalog) → "Free".
    const zeroLabel = price !== undefined ? INSTANCE_ZERO_PRICE_LABEL : ZERO_PRICE_LABEL
    return formatSubscriptionPrice(price ?? pass.price, { zeroLabel }).text
  }, [price, pass.price])

  const labelledById = useId()

  return (
    <Card
      as="article"
      rounded="3xl"
      aria-labelledby={labelledById}
      className={cn(
        'group relative flex min-h-52 overflow-hidden bg-white transition-transform hover:scale-[1.02]',
        variant === 'my-plan' ? 'border border-1.5 border-butter-gray-100' : 'border-butter-gray-200 shadow-sm',
        'focus-within:ring-2 focus-within:ring-brand-600 focus-within:ring-offset-2',
        variant === 'reporting' && 'min-h-0 border-2 border-butter-gray-100 bg-butter-gray-25 shadow-none hover:scale-100',
        className,
      )}
      data-testid={`subscription-pass-plan-card-select-button-${pass.id}`}
    >
      {/* Interactive overlay covering the whole card — only rendered when `onClick`
          is wired, so display-only cards have no dead, mislabelled tab-stop. */}
      {onClick && (
        <button
          type="button"
          onClick={() => onClick(pass)}
          aria-haspopup={opensDialog ? 'dialog' : undefined}
          aria-label={overlayLabel ?? `Edit ${pass.name}`}
          className="absolute inset-0 z-20 focus:outline-none"
        />
      )}

      <div className={cn("flex min-w-0 flex-1 flex-col gap-4 p-6 justify-between", variant === 'reporting' && 'gap-3 p-7')}>
        <div>
          {badge && (
            <span className={
              variant === 'my-plan'
                ? 'mb-3 inline-flex w-14 items-center justify-center rounded-full bg-white py-1 text-xs leading-none font-medium text-butter-gray-600 shadow-sm border border-butter-gray-200'
                : 'mb-2 inline-block rounded-full bg-white px-3 py-1 text-xs font-medium text-butter-gray-900 shadow-sm border border-butter-gray-200'
            }>
              {badge}
            </span>
          )}
          <h3 id={labelledById} className={variant === 'reporting' ? 'text-[22px] font-semibold leading-[26px] text-butter-gray-800' : variant === 'my-plan' ? 'text-lg font-semibold leading-tight text-butter-gray-900' : 'text-md font-medium text-butter-gray-950'} data-testid={`subscription-pass-plan-card-title-${pass.id}`}>
            {pass.name}
          </h3>
        </div>

      <div className={cn("flex flex-col gap-4", variant === 'reporting' && 'gap-6')}>
        <p className={variant === 'reporting' ? 'text-base leading-5 text-butter-gray-600' : 'text-sm text-butter-gray-500 line-clamp-2'}>
          {pass.description}
        </p>

        <div className={variant === 'reporting' ? 'text-[28px] font-semibold leading-8 text-butter-gray-800' : variant === 'my-plan' ? 'text-lg font-semibold text-butter-gray-900' : 'text-xl font-semibold text-butter-gray-950'} data-testid={`subscription-pass-plan-card-price-${pass.id}`}>
          {formattedPrice ?? priceText}
        </div>
      </div>
      </div>

      <div className="relative flex w-12 shrink-0 self-stretch">
        <GradientOverlay
          gradient={gradient}
          to="bottom-right"
          dotsPosition="bottom-right"
          className="rounded-r-3xl"
        />
        <PassOrnamentIcon className={cn("absolute left-1/2 top-6 z-10 h-6 w-6 -translate-x-1/2 text-white/80", variant === 'reporting' && 'top-6 size-5')} />
      </div>
    </Card>
  )
}
